import React, { useState } from "react";
import { X, Info, AlertTriangle, AlertOctagon, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type BannerLevel = "info" | "warning" | "error";

interface SystemBannerProps {
  message: string;
  level: BannerLevel;
}

const ICONS: Record<BannerLevel, LucideIcon> = {
  info: Info,
  warning: AlertTriangle,
  error: AlertOctagon,
};

/**
 * Full-width, system-wide announcement bar shown at the top of the app
 * (maintenance windows, service status). Styled by severity from `level`:
 * brand-neutral info, amber warning, red error — with a leading icon, inked
 * text, a hairline bottom rule, and a dismiss control. Replaces the previous
 * always-yellow bar that ignored its level.
 */
export const SystemBanner = ({ message, level }: SystemBannerProps) => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  const Icon = ICONS[level] ?? Info;

  return (
    <div
      className={cn("vb-system-banner", `vb-system-banner--${level}`)}
      role={level === "error" ? "alert" : "status"}
      aria-live={level === "error" ? "assertive" : "polite"}
    >
      <span className="vb-system-banner__icon" aria-hidden="true">
        <Icon size={16} />
      </span>
      <p className="vb-system-banner__msg">{message}</p>
      <button
        type="button"
        onClick={() => setIsVisible(false)}
        className="vb-system-banner__dismiss"
        aria-label="Dismiss announcement"
      >
        <X size={16} />
      </button>
    </div>
  );
};
